// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the columns the desktop task list can show, like ClickUp's Fields
// panel: ClickUp's own task fields plus every custom field the tasks in view
// carry. Custom fields are keyed by name, so "Billability" is one column even
// when several spaces each define their own Billability field; a chosen field
// column only appears in views whose tasks have that field.

import type {KonvoTask, KonvoTaskField} from '@app/features/konvo_tasks/state/KonvoTasks';

export type BuiltInColumn =
	| 'status'
	| 'assignee'
	| 'due'
	| 'start'
	| 'priority'
	| 'created'
	| 'updated'
	| 'closed'
	| 'estimate'
	| 'time'
	| 'tags'
	| 'creator'
	| 'list'
	| 'id';

export type FieldColumnKey = `field:${string}`;
export type ColumnKey = BuiltInColumn | FieldColumnKey;

/** Built-in fields in the order the Fields panel lists them. */
export const BUILT_IN_COLUMNS: ReadonlyArray<BuiltInColumn> = [
	'status',
	'assignee',
	'due',
	'start',
	'priority',
	'tags',
	'estimate',
	'time',
	'created',
	'updated',
	'closed',
	'creator',
	'list',
	'id',
];

/** What a new user sees (the list's columns before the Fields panel existed). */
export const DEFAULT_COLUMNS: ReadonlyArray<ColumnKey> = ['assignee', 'due', 'priority', 'id', 'time'];

export function isBuiltInColumn(key: string): key is BuiltInColumn {
	return (BUILT_IN_COLUMNS as ReadonlyArray<string>).includes(key);
}

export function isFieldColumn(key: string): key is FieldColumnKey {
	return key.startsWith('field:') && key.length > 'field:'.length;
}

export function isColumnKey(key: unknown): key is ColumnKey {
	return typeof key === 'string' && (isBuiltInColumn(key) || isFieldColumn(key));
}

export function fieldColumnKey(name: string): FieldColumnKey {
	return `field:${name.trim().toLowerCase()}`;
}

/** A custom field column: every same-named field on the tasks in view. */
export interface FieldColumn {
	key: FieldColumnKey;
	/** The name as ClickUp spells it (from the first task that has it). */
	name: string;
	/** The type most of these fields have, for the panel icon and column width. */
	type: string;
	/** How many tasks in view have a value. */
	filled: number;
}

/** Custom field columns for the tasks in view, by name. */
export function fieldColumnsFor(tasks: ReadonlyArray<KonvoTask>): Array<FieldColumn> {
	const byKey = new Map<FieldColumnKey, FieldColumn & {types: Map<string, number>}>();
	for (const task of tasks) {
		for (const field of task.custom_fields ?? []) {
			if (!field.name?.trim()) continue;
			const key = fieldColumnKey(field.name);
			let entry = byKey.get(key);
			if (!entry) {
				entry = {key, name: field.name.trim(), type: field.type, filled: 0, types: new Map()};
				byKey.set(key, entry);
			}
			entry.types.set(field.type, (entry.types.get(field.type) ?? 0) + 1);
			if (field.type === 'checkbox' ? field.value === true : hasFieldValue(field)) entry.filled += 1;
		}
	}
	return [...byKey.values()]
		.map(({types, ...column}) => ({
			...column,
			type: [...types.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? column.type,
		}))
		.sort((a, b) => a.name.localeCompare(b.name));
}

/** The task's field behind a field column, if its list has one. */
export function taskFieldFor(task: KonvoTask, key: FieldColumnKey): KonvoTaskField | undefined {
	return task.custom_fields?.find((field) => field.name && fieldColumnKey(field.name) === key);
}

export function hasFieldValue(field: KonvoTaskField | undefined): boolean {
	if (!field) return false;
	const {value} = field;
	if (value == null || value === '') return false;
	if (Array.isArray(value)) return value.length > 0;
	return true;
}

const BUILT_IN_WIDTH: Record<BuiltInColumn, string> = {
	status: '136px',
	assignee: '112px',
	due: '108px',
	start: '108px',
	priority: '100px',
	created: '108px',
	updated: '120px',
	closed: '108px',
	estimate: '96px',
	// Tracked time and the hover timer button.
	time: '80px 36px',
	tags: '160px',
	creator: '132px',
	list: '160px',
	id: '104px',
};

const FIELD_WIDTH: Record<string, string> = {
	drop_down: '140px',
	labels: '180px',
	users: '112px',
	number: '104px',
	currency: '112px',
	emoji: '104px',
	date: '108px',
	checkbox: '88px',
	manual_progress: '120px',
	automatic_progress: '120px',
	attachment: '88px',
	short_text: '160px',
	text: '200px',
	url: '180px',
	email: '180px',
	phone: '140px',
	location: '180px',
	formula: '120px',
	tasks: '160px',
	list_relationship: '160px',
};

export function columnWidth(key: ColumnKey, fieldType?: string): string {
	if (isBuiltInColumn(key)) return BUILT_IN_WIDTH[key];
	return FIELD_WIDTH[fieldType ?? ''] ?? '140px';
}

/** Pixel width of the grid tracks (for the table's minimum width). */
export function columnPixels(tracks: string): number {
	return tracks
		.split(/\s+/)
		.map((track) => Number.parseFloat(track))
		.filter((value) => Number.isFinite(value))
		.reduce((sum, value) => sum + value, 0);
}
